import { _ } from '@sveltia/i18n';
import { isObject } from '@sveltia/utils/object';
import { LocalStorage } from '@sveltia/utils/storage';

import { goto, parseLocation } from '$lib/services/app/navigation';
import { backend, backendName, selectBackend } from '$lib/services/backends';
import { lockedBranch, mergeLockedBranch } from '$lib/services/backends/branch-access';
import { NOT_COLLABORATOR_ERROR_MESSAGE } from '$lib/services/backends/git/shared/errors';
import { repositoryHead } from '$lib/services/backends/git/shared/fetch';
import { startRemoteChangePolling, stopRemoteChangePolling } from '$lib/services/backends/poll';
import { cmsConfig } from '$lib/services/config';
import { dataLoaded } from '$lib/services/contents';
import { arrayFileItems } from '$lib/services/contents/file/process';
import { resetDeployments } from '$lib/services/deployments';
import { resetPageLiveness } from '$lib/services/deployments/ping';
import { initDeployments } from '$lib/services/deployments/resolve';
import { user } from '$lib/services/user/account.svelte';
import { USER_STORAGE_KEY } from '$lib/services/user/constants';
import { prefs } from '$lib/services/user/prefs.svelte';
import {
  publishingBranches,
  unpublishedEntries,
  unpublishedEntriesLoaded,
} from '$lib/services/workflow';
import { resetDeployingEntries } from '$lib/services/workflow/deploy';
import { loadUnpublishedEntries, startLoadingPullRequests } from '$lib/services/workflow/load';
import {
  forkedRepository,
  forkPermissionRequest,
  openAuthoringInitialized,
} from '$lib/services/workflow/open-authoring';

/**
 * @import { BackendService, InternalCmsConfig, User } from '$lib/types/private';
 */

/**
 * Context of the sign-in error.
 * @typedef {'authentication' | 'dataFetch'} SignInErrorContext
 */

const MAGIC_LINK_REGEX = /^\/signin\/(?<encodedData>.+)/;

export const auth = $state({
  /** @type {{ message: string, context: SignInErrorContext }} */
  signInError: { message: '', context: 'authentication' },
  /** Whether the user is not authenticated. */
  unauthenticated: true,
  /** Whether a sign-in is in progress. */
  signingIn: false,
  /**
   * Account that a magic link signs the user in to, while they’re asked whether to go ahead. The
   * link can be crafted by anyone, so it’s only used once the user has recognized the account.
   * @type {{ account: User, resolve: (confirmed: boolean) => void } | undefined}
   */
  magicLinkConfirmation: undefined,
});

/**
 * Clear the cached user token so the sign-in form is shown on next load. The user caches left by
 * Netlify/Decap CMS, which may also hold a token, are removed as well.
 */
const clearUserCache = async () => {
  await LocalStorage.set(USER_STORAGE_KEY, {});
  await LocalStorage.delete('decap-cms-user');
  await LocalStorage.delete('netlify-cms-user');
  user.account = undefined;
  auth.unauthenticated = true;
};

/**
 * Remove the API keys and log-in credentials for integrations, such as translation services and
 * cloud storage, from the user preferences, so they don’t remain on the device once the user has
 * signed out. Other preferences, like the theme and language, are kept. This is only done on an
 * explicit sign-out, not when an expired or revoked token sends the user back to the sign-in form.
 */
const clearIntegrationCredentials = () => {
  prefs.apiKeys = {};
  delete prefs.logins;
};

/**
 * Clear the cached token for authentication/access errors so the sign-in form is shown instead of a
 * dead-end error. Don’t clear for configuration errors like missing branches or repositories where
 * the credentials are still valid.
 * @param {Error} error Exception to check if it’s an authentication error.
 */
const clearUserCacheIfNeeded = async (error) => {
  const isAuthError =
    typeof (/** @type {any} */ (error?.cause)?.status) === 'number' ||
    error?.message === NOT_COLLABORATOR_ERROR_MESSAGE;

  if (isAuthError) {
    await clearUserCache();
  }
};

/**
 * Reset the sign-in error.
 */
export const resetError = () => {
  auth.signInError = { message: '', context: 'authentication' };
};

/**
 * Log an authentication error on the UI and in the browser console.
 * @param {Error} ex Exception.
 * @param {SignInErrorContext} [context] Context of the error.
 */
export const logError = (ex, context = 'authentication') => {
  let message = /** @type {{ message: string }} */ (ex.cause)?.message || _('unexpected_error');

  if (ex.name === 'NotFoundError') {
    message = _('sign_in_error.not_project_root');
  }

  if (ex.name === 'AbortError') {
    message = _(
      backendName.current === 'local'
        ? 'sign_in_error.picker_dismissed'
        : 'sign_in_error.authentication_aborted',
    );
  }

  auth.signInError = { message, context };
  // eslint-disable-next-line no-console
  console.error(ex.name, ex.message, ex.cause);
};

/**
 * Parse a magic link URL generated for QR code sign-in to extract the token and preferences.
 * @returns {{ _user: { token: string } | undefined, copiedPrefs: Record<string, any> | undefined
 * }} Object containing the user token and copied preferences.
 */
export const parseMagicLink = () => {
  const { path } = parseLocation();
  const { encodedData } = path.match(MAGIC_LINK_REGEX)?.groups ?? {};

  if (!encodedData) {
    return { _user: undefined, copiedPrefs: undefined };
  }

  // Remove token from the URL
  goto('', { replaceState: true });

  /** @type {{ token: string } | undefined} */
  let _user = undefined;
  /** @type {Record<string, any> | undefined} */
  let copiedPrefs = undefined;

  try {
    const data = JSON.parse(atob(encodedData));

    if (isObject(data) && typeof data.token === 'string') {
      _user = { token: data.token };

      if (isObject(data.prefs)) {
        copiedPrefs = data.prefs;
      }
    }
  } catch {
    //
  }

  return { _user, copiedPrefs };
};

/**
 * Find cached user info, including a compatible Netlify/Decap CMS user object.
 * @returns {Promise<Record<string, any> | undefined>} Cached user info, or undefined if not found.
 */
export const getUserCache = async () => {
  const userCache =
    (await LocalStorage.get(USER_STORAGE_KEY)) ||
    (await LocalStorage.get('decap-cms-user')) ||
    (await LocalStorage.get('netlify-cms-user'));

  return isObject(userCache) && typeof userCache.backendName === 'string' ? userCache : undefined;
};

/**
 * Get the backend instance based on the cached user info or site config.
 * @param {Record<string, any> | undefined} _user Cached user info.
 * @returns {BackendService | undefined} Backend instance to be used.
 */
export const getBackend = (_user) => {
  // Determine the backend name based on the user cache or site config. Use the local backend if the
  // user cache is found and the backend name is `local`, which is used by Sveltia CMS, or `proxy`,
  // which is used by Netlify/Decap CMS when running the local proxy server. Otherwise, simply use
  // the backend name from the site config. This is to ensure that the user is signed in with the
  // correct backend, especially when the user cache is from a different backend than the current
  // site config.
  const _backendName =
    _user?.backendName === 'local' || _user?.backendName === 'proxy'
      ? 'local'
      : /** @type {InternalCmsConfig} */ (cmsConfig.current).backend.name;

  return selectBackend(_backendName);
};

/**
 * Verify the token passed with a magic link, and ask the user to confirm the account it belongs
 * to. Since anyone can craft a link, it could otherwise sign the user in to someone else’s account
 * without their knowledge, overwriting their session, and copy the settings in the link, such as a
 * deploy hook URL, to their device.
 * @param {string} token Token passed with the link.
 * @returns {Promise<User | undefined>} User info if the token is valid and the user has confirmed
 * the account, or `undefined` otherwise.
 */
export const confirmMagicLink = async (token) => {
  const _backend = getBackend(undefined);

  if (!_backend) {
    return undefined;
  }

  /** @type {User | undefined} */
  let account = undefined;

  auth.signingIn = true;

  try {
    // The user store is left untouched, so an existing session stays cached until the user agrees
    // to replace it
    account = /** @type {User | undefined} */ (await _backend.signIn({ token, auto: true }));
  } catch {
    //
  }

  auth.signingIn = false;

  if (!account) {
    return undefined;
  }

  const confirmed = await new Promise((resolve) => {
    auth.magicLinkConfirmation = { account: /** @type {User} */ (account), resolve };
  });

  auth.magicLinkConfirmation = undefined;

  return confirmed ? account : undefined;
};

/**
 * Copy the user preferences passed with a magic link. The deploy hook URL and its `Authorization`
 * header are only copied together, so a link that has a URL but no header can’t leave the header
 * already on this device to be sent to the new URL.
 * @param {Record<string, any>} copiedPrefs Preferences passed with the link.
 */
export const copyMagicLinkPrefs = (copiedPrefs) => {
  if ('deployHookURL' in copiedPrefs) {
    delete prefs.deployHookAuthHeader;
  }

  Object.assign(prefs, copiedPrefs);
};

/**
 * Load the files and unpublished entries from the backend the user has just signed in to, then
 * start keeping them up to date.
 * @param {BackendService} _backend Backend.
 */
const loadRepositoryData = async (_backend) => {
  // The pull requests don’t depend on the files, so they’re requested at the same time
  const pullRequests = startLoadingPullRequests();

  await _backend.fetchFiles();
  await loadUnpublishedEntries(pullRequests);
  // The deploy state is a nicety, so it’s resolved in the background rather than delaying the UI
  initDeployments();
  // From here on, someone else’s commits are picked up as they land
  startRemoteChangePolling();
};

/**
 * Check if the user info is cached, set the backend, and automatically start loading files if the
 * backend is Git-based and user’s auth token is found.
 */
export const signInAutomatically = async () => {
  resetError();

  const { _user: magicLinkUser, copiedPrefs } = parseMagicLink();
  /** @type {Record<string, any> | undefined} */
  let _user = magicLinkUser ? await confirmMagicLink(magicLinkUser.token) : undefined;
  // The account has been verified already if the user has accepted a magic link
  const verified = !!_user;

  // Fall back to the cached user, which also covers a declined or invalid magic link
  _user ??= await getUserCache();

  // Initialize the backend, which is needed on the login page
  const _backend = getBackend(_user);

  // If no cached user info is found, simply return as we cannot sign in automatically
  if (!_user) {
    return;
  }

  if (!verified && _backend) {
    // Temporarily populate the `user` store with the cache, otherwise it’s not updated in
    // `refreshAccessToken`
    user.account = /** @type {User} */ (_user);

    const { token, refreshToken } = _user;

    auth.signingIn = true;

    try {
      _user = /** @type {User} */ (await _backend.signIn({ token, refreshToken, auto: true }));
    } catch {
      // The local backend may throw if the file handle permission is not given
      _user = undefined;
      user.account = undefined;
    }
  }

  auth.signingIn = false;
  auth.unauthenticated = !_user;

  if (!_user || !_backend) {
    return;
  }

  // Use the cached user to start fetching files
  user.account = /** @type {User} */ (_user);

  // Copy user preferences passed with QR code
  if (verified && copiedPrefs) {
    copyMagicLinkPrefs(copiedPrefs);
  }

  try {
    await loadRepositoryData(_backend);
  } catch (/** @type {any} */ ex) {
    // The API request may fail if the cached token has been expired or revoked. Then let the user
    // sign in again. 404 Not Found is also considered an authentication error.
    // https://docs.github.com/en/rest/overview/troubleshooting-the-rest-api#404-not-found-for-an-existing-resource
    if ([401, 403, 404].includes(ex.cause?.status)) {
      auth.unauthenticated = true;
    } else {
      logError(ex, 'dataFetch');
    }

    await clearUserCacheIfNeeded(ex);
  }
};

/**
 * Sign in with the given backend.
 * @param {string} _backendName Backend name to be used.
 * @param {string} [token] Personal Access Token (PAT) to be used for authentication.
 */
export const signInManually = async (_backendName, token) => {
  resetError();

  const _backend = selectBackend(_backendName);

  if (!_backend) {
    return;
  }

  let _user;

  auth.signingIn = true;

  try {
    _user = await _backend.signIn({ token, auto: false });
  } catch (/** @type {any} */ ex) {
    auth.signingIn = false;
    auth.unauthenticated = true;

    if (!!token && ex.cause?.status === 401) {
      // If the user is signing in using a personal access token (PAT) and the token is invalid,
      // display a specific error message.
      logError(
        new Error('Invalid token', { cause: { message: _('sign_in_error.invalid_token') } }),
      );
    } else {
      logError(ex);
    }

    return;
  }

  auth.signingIn = false;
  auth.unauthenticated = !_user;

  if (!_user) {
    return;
  }

  user.account = _user;

  try {
    await loadRepositoryData(_backend);
  } catch (/** @type {any} */ ex) {
    logError(ex, 'dataFetch');
    await clearUserCacheIfNeeded(ex);
  }
};

/**
 * Sign out from the current backend.
 */
export const signOut = async () => {
  stopRemoteChangePolling();
  await backend.current?.signOut();
  await clearUserCache();
  clearIntegrationCredentials();

  selectBackend(undefined);
  dataLoaded.current = false;
  // The items of the files storing all the entries of a collection belong to this repository
  arrayFileItems.clear();
  repositoryHead.current = '';
  lockedBranch.current = undefined;
  mergeLockedBranch.current = undefined;
  unpublishedEntries.current = [];
  unpublishedEntriesLoaded.current = false;
  publishingBranches.current = [];
  // The fork belongs to the signed-out user, so the next user needs Open Authoring set up again
  forkPermissionRequest.current?.respond(false);
  forkedRepository.current = undefined;
  openAuthoringInitialized.current = false;
  resetDeployingEntries();
  resetDeployments();
  resetPageLiveness();

  const redirectURL = cmsConfig.current?.logout_redirect_url;

  if (redirectURL) {
    window.location.href = redirectURL;
  }
};
